/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useState } from 'react';
import { Button, ButtonGroup, Heading, Icon, SecurityHelper, Text } from 'sarsaparilla';
import type { aiReview } from '../model/publicReview';

type PropTypes = {
    review: aiReview;
    voteReview: Function;
    openLoginModal: Function;
};

export default function AIReview({ review, voteReview, openLoginModal }: PropTypes) {
    const [changedHelpfulVotesCount, setChangedHelpfulVotesCount] =
        useState<boolean>(false);
    const [changedNotHelpfulVotesCount, setChangedNotHelpfulVotesCount] =
        useState<boolean>(false);
    const [helpfulVotesCount, setHelpfulVotesCount] = useState<number>(
        review.helpful_votes_count
    );
    const [notHelpfulVotesCount, setNotHelpfulVotesCount] = useState<number>(
        review.not_helpful_votes_count
    );

    const voteValue = localStorage.getItem(review.location_id);

    const reviewText = () => {
        return review.summary;
    };

    const handleVote = (is_helpful: boolean, vote_value: string | null) => {
        if (!SecurityHelper.isLoggedIn()) {
            openLoginModal();
            return;
        }

        const { location_id, location_type } = review;
        let newHelpfulVotesCounts = helpfulVotesCount;
        let newNotHelpfulVotesCounts = notHelpfulVotesCount;
        let vote_changed = false;

        if (vote_value !== null) {
            if (vote_value === 'true') {
                if (is_helpful === false) {
                    // changed from helpful to not helpful
                    setChangedNotHelpfulVotesCount(true);
                    newHelpfulVotesCounts -= 1;
                    newNotHelpfulVotesCounts += 1;
                } else {
                    // "yes" pressed twice, no change
                    return;
                }
            } /* vote_value == "false" */ else if (is_helpful === true) {
                // changed from not helpful to helpful
                setChangedHelpfulVotesCount(true);
                newHelpfulVotesCounts += 1;
                newNotHelpfulVotesCounts -= 1;
            } else {
                // "no" pressed twice, no change
                return;
            }
            vote_changed = true;
        } else if (vote_value == null) {
            // New vote cast
            if (is_helpful === true) {
                setChangedHelpfulVotesCount(true);
                newHelpfulVotesCounts += 1;
            } else {
                setChangedNotHelpfulVotesCount(true);
                newNotHelpfulVotesCounts += 1;
            }
        }

        setHelpfulVotesCount(newHelpfulVotesCounts);
        setNotHelpfulVotesCount(newNotHelpfulVotesCounts);
        voteReview({ location_id, location_type, is_helpful, vote_changed });
    };

    return (
        <div className="ai-review-wrapper">
            <Heading headingLevel={3} appearance="h5">
                What visitors are saying:
            </Heading>
            <p data-notranslate>{reviewText()}</p>
            <Text size="xs">AI-generated from the text of visitor reviews</Text>
            <ButtonGroup isFullWidthOnMobile={false}>
                <i className="mr-2">Was this review helpful?</i>
                <Button
                    appearance="tertiary"
                    size="xs"
                    isDisabled={changedHelpfulVotesCount}
                    onClick={() => handleVote(true, voteValue)}
                    screenReaderTextBefore="Vote review helpful"
                    aria-label={`Yes, ${helpfulVotesCount} people`}
                    gaTrackingId="304665831973"
                >
                    {`Yes (${helpfulVotesCount})`}
                </Button>
                <Button
                    appearance="tertiary"
                    size="xs"
                    isDisabled={changedNotHelpfulVotesCount}
                    onClick={() => handleVote(false, voteValue)}
                    screenReaderTextBefore="Vote review unhelpful"
                    aria-label={`No, ${notHelpfulVotesCount} people`}
                    gaTrackingId="304665831973"
                >
                    {`No (${notHelpfulVotesCount})`}
                </Button>
            </ButtonGroup>
        </div>
    );
}
